import { Formik } from 'formik'
import React, { useContext, useState } from 'react'
import { CustomerType, Maybe } from '../../../common/types'
import { Page } from '../../../components/layout'
import { dismissNotifications, setErrorNotification } from '../../../components/notification'
import { Modal } from '../../../components/Modal'
import { Loading } from '../../../components/responses'
import { useHistoryPush } from '../../../components/router'
import { TopNavigation } from '../../../components/TopNavigation/TopNavigation'
import { setOwnersHoldersData } from '../../../components/VehicleDetails/functions'
import { OwnersHoldersTrafi } from '../../../components/VehicleDetails/types'
import { VehicleInfo } from '../../../components/VehicleSearchModal/types'
import {
  GenericStorageRetrieveFn,
  persistentDelete,
  persistentGet,
  persistentSave,
} from '../../../util/localStorage/localStorage'
import { useFormikHelpers } from '../../../util/useFormikHelpers'
import { CustomerFormValues } from '../../Customer/types'
import { StateStore } from '../../StoreProvider'
import { STORED_ADD_TOWING_ORDER } from '../../StoreProvider/OfflineMode'
import { shouldDisableOperator } from '../EditTowingOrder/EditTowingOrderForm'
import { VisibilityType } from '../types'
import { getInitialValues, initialVehicleDetails } from './initialValues'
import { TowingOrderFormContents } from './TowingOrderFormContents'
import { AddTowingOrderFormValues } from './types'
import { formatMessageSafe } from '../../../util/translateInputOptions'
import { useIntl } from 'react-intl'
import { useSettings } from '../../Parameters'
import { messages } from '../messages'

const TOWING_ORDER = STORED_ADD_TOWING_ORDER

interface FormProps {
  addTowingOrder: (values: AddTowingOrderFormValues) => Promise<any>
  loading: boolean
  isSos: boolean
}

interface AddTowingOrderContentProps {
  closeForm: () => void
  formatMessage: any
  operatorDisabled: boolean
  storeFormValues: boolean
  setStoreFormValues: (v: boolean) => void
  isSos: boolean
  getOperatorId: () => number | null
  ownersHolders: OwnersHoldersTrafi[]
  setOwnersHolders: React.Dispatch<React.SetStateAction<OwnersHoldersTrafi[]>>
}

const AddTowingOrderContent: React.FunctionComponent<AddTowingOrderContentProps> = ({
  closeForm,
  formatMessage,
  operatorDisabled,
  storeFormValues,
  isSos,
  getOperatorId,
  ownersHolders,
  setOwnersHolders,
}) => {
  const { values, setValues, getValue, setFieldValue, isSubmitting, handleSubmit } =
    useFormikHelpers<AddTowingOrderFormValues>()
  const settings = useSettings()
  const [showPaymentLinkConfirm, setShowPaymentLinkConfirm] = useState(false)

  const onSetOrderer = (o: CustomerFormValues) => {
    const currentOrdererType = values.orderer.type
    values.orderer = Object.assign({}, o)
    values.orderer.type = currentOrdererType

    if (settings?.notifications?.enabled) {
      switch (o.type) {
        case CustomerType.person:
          if (o.personCustomer) {
            if (o.personCustomer.notificationMethod === 'default') {
              values.notificationMethod = settings.notifications.method
            } else {
              values.notificationMethod = o.personCustomer.notificationMethod
            }
          }
          break
        case CustomerType.company:
          if (o.companyCustomer) {
            if (o.companyCustomer.notificationMethod === 'default') {
              values.notificationMethod = settings.notifications.method
            } else {
              values.notificationMethod = o.companyCustomer.notificationMethod
            }
          }
          break
      }
    }

    setValues(values)
  }

  const setOrdererFromTrafi = (o: CustomerFormValues) => {
    values.orderer = Object.assign({}, o)
    setValues(values)
  }

  const onHandleVehicleResults = (res: VehicleInfo) => {
    if (res) {
      setOwnersHoldersData(res, setOwnersHolders)
      const owner = res.ownersHolders.find(ownerHolder => ownerHolder.ownershipType === 1) || null
      const holder = res.ownersHolders.find(ownerHolder => ownerHolder.ownershipType === 3) || null
      values.vehicleDetails.registrationNumber = res.vehicle.registrationNumber
      values.vehicleDetails.insuranceCompany = res.insurance.insuranceCompany
      values.vehicleDetails.makeAndModel = res.vehicle.makeAndModel
      values.vehicleDetails.owner = owner && owner.customer && owner.customer.name ? owner.customer.name : ''
      values.vehicleDetails.vehicleClass = res.vehicle.vehicleClass
      values.vehicleDetails.holder =
        holder && holder.customer && holder.customer.name ? holder.customer.name : ''
      values.vehicleInfoCheckId = res.id

      values.vehicleDetails.dataSource = res.searchMeta ? res.searchMeta.dataSource : ''
      values.vehicleDetails.dataSourceLongText = res.searchMeta ? res.searchMeta.dataSourceLongText : ''
      values.vehicleDetails.dataSourceShortText = res.searchMeta ? res.searchMeta.dataSourceShortText : ''

      if (isSos && values.sosServiceOrder) {
        values.sosServiceOrder.insuranceCompany = res.insurance.insuranceCompany
      }

      setValues(values)
    }
  }

  const onCancelVehicleDetails = () => {
    setFieldValue('vehicleDetails', initialVehicleDetails)
    setFieldValue('vehicleInfoCheckId', null)
  }

  if (isSubmitting ? false : storeFormValues ? true : false) {
    persistentSave(TOWING_ORDER, values)
  }

  const handleSubmitForm = (assign: boolean, operatorId: Maybe<number>, visibility: Maybe<VisibilityType>) => {
    dismissNotifications()
    values.assignOnSave = assign

    if (operatorId) {
      values.operatorId = operatorId
    } else {
      values.operatorId = assign ? getOperatorId() : null
    }

    if (visibility) {
      values.visibility = visibility
    }

    if (values.prepaymentType === 'online_payment') {
      if (!values.prepaymentPaymentDetails?.receiptEmail?.trim()) {
        return setErrorNotification(formatMessage(messages.errorNotificationTitle), formatMessage(messages.paymentLinkMissingReceiptEmail))
      }
      if (!values.prepaymentPaymentDetails?.debtorPhone?.trim()) {
        return setErrorNotification(formatMessage(messages.errorNotificationTitle), formatMessage(messages.paymentLinkMissingPhone))
      }
      setShowPaymentLinkConfirm(true)
      return
    }

    persistentSave(TOWING_ORDER, values)
    handleSubmit()
  }

  const confirmAndSubmit = () => {
    setShowPaymentLinkConfirm(false)
    persistentSave(TOWING_ORDER, values)
    handleSubmit()
  }

  return (
    <>
      {showPaymentLinkConfirm && (
        <Modal
          label={formatMessage(messages.sendPaymentLinkModalLabel)}
          message={formatMessage(messages.sendPaymentLinkModalMessage)}
          confirmButtonCategory="save"
          onConfirm={confirmAndSubmit}
          close={() => setShowPaymentLinkConfirm(false)}
          closeLabel={formatMessage({ id: 'button.cancel' })}
          confirmLabel={formatMessage(messages.sendPaymentLinkModalConfirm)}
        />
      )}
      <TowingOrderFormContents
        submitForm={handleSubmitForm}
        closeForm={closeForm}
        formatMessage={formatMessage}
        onSetOrderer={onSetOrderer}
        onHandleVehicleResults={onHandleVehicleResults}
        onCancelVehicleDetails={onCancelVehicleDetails}
        showTowingDateSpan={values.jobDetails.towingDateType === 'span'}
        orderer={values.orderer}
        ssn={
          values.orderer.personCustomer && values.orderer.personCustomer.ssn
            ? values.orderer.personCustomer.ssn
            : ''
        }
        businessId={
          values.orderer.companyCustomer && values.orderer.companyCustomer.businessId
            ? values.orderer.companyCustomer.businessId
            : ''
        }
        registrationNumber={values.vehicleDetails.registrationNumber}
        operatorDisabled={operatorDisabled}
        setFieldValue={setFieldValue}
        getValue={getValue}
        isSos={isSos}
        ownersHolders={ownersHolders}
        setOrdererFromTrafi={setOrdererFromTrafi}
      />
    </>
  )
}

export const TowingOrderFormIntl: React.FunctionComponent<FormProps> = ({
  addTowingOrder,
  loading,
  isSos,
}) => {
  const [storeFormValues, setStoreFormValues] = useState<boolean>(true)
  const [ownersHolders, setOwnersHolders] = useState<OwnersHoldersTrafi[]>([])
  const historyPush = useHistoryPush()
  const intl = useIntl()
  const formatMessage = formatMessageSafe(intl)
  const closeForm = () => {
    persistentDelete(TOWING_ORDER)
    historyPush('/')
  }

  const persistenGetTowingOrder: GenericStorageRetrieveFn<AddTowingOrderFormValues> = persistentGet

  const storedTowingOrder = persistenGetTowingOrder(TOWING_ORDER)
  if (storedTowingOrder) {
    storedTowingOrder.jobDetails.towingDate = new Date()
  }

  const { state } = useContext(StateStore)
  const settings = useSettings()

  const formValues: AddTowingOrderFormValues = getInitialValues(isSos, settings)

  const getOperatorId = () => {
    if (state && state.currentUser && settings && settings.operators) {
      const currentUser = state.currentUser
      if (settings.operators.find((operator: any) => operator.id === currentUser.user_id)) {
        return state.currentUser.user_id
      }
    }
    return null
  }

  const operatorDisabled = shouldDisableOperator(state.currentUser)

  return (
    <Page noPadding>
      <Loading loading={loading} />
      <TopNavigation
        label={formatMessage({ id: 'routeSubform.cancel' })}
        address="/towing-jobs"
        onNavigate={() => {
          setStoreFormValues(false)
          persistentDelete(TOWING_ORDER)
        }}
      />
      <Formik
        initialValues={formValues}
        validateOnChange={false}
        onSubmit={(values: AddTowingOrderFormValues) => addTowingOrder(values)}
      >
        {() => (
          <AddTowingOrderContent
            closeForm={closeForm}
            formatMessage={formatMessage}
            operatorDisabled={operatorDisabled}
            storeFormValues={storeFormValues}
            setStoreFormValues={setStoreFormValues}
            isSos={isSos}
            getOperatorId={getOperatorId}
            ownersHolders={ownersHolders}
            setOwnersHolders={setOwnersHolders}
          />
        )}
      </Formik>
    </Page>
  )
}

export const TowingOrderForm = TowingOrderFormIntl
